import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconAlertCircle, IconMail, IconLock, IconUser,
  IconEye, IconEyeOff, IconShieldCheck, IconCheck,
} from '@tabler/icons-react';
import api from '../api/api';

export default function SignupPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '', passwordConfirm: '', name: '', nickname: '' });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [siteConfig, setSiteConfig] = useState({ 'site.name': 'AI65 Neo', 'site.description': '', 'site.loginColor': 'blue' });

  const LOGIN_COLOR_GRADIENTS = {
    blue:   'linear-gradient(135deg, #206bc4 0%, #4263eb 50%, #ae3ec9 100%)',
    green:  'linear-gradient(135deg, #2d6a4f 0%, #52b788 100%)',
    red:    'linear-gradient(135deg, #8b2635 0%, #c0392b 100%)',
    purple: 'linear-gradient(135deg, #4a2c8a 0%, #6741d9 100%)',
    black:  'linear-gradient(135deg, #212529 0%, #495057 100%)',
    gray:   'linear-gradient(135deg, #868e96 0%, #adb5bd 100%)',
  };
  const LOGIN_COLOR_PRIMARY = {
    blue: '#206bc4', green: '#2d6a4f', red: '#8b2635',
    purple: '#4a2c8a', black: '#212529', gray: '#868e96',
  };
  const loginColor = siteConfig['site.loginColor'] || 'blue';
  const loginGradient = LOGIN_COLOR_GRADIENTS[loginColor] || LOGIN_COLOR_GRADIENTS.blue;
  const loginPrimary = LOGIN_COLOR_PRIMARY[loginColor] || LOGIN_COLOR_PRIMARY.blue;

  useEffect(() => {
    api.get('/system-config').then(res => {
      setSiteConfig(prev => ({ ...prev, ...res.data }));
      if (res.data['site.favicon']) { let l = document.querySelector("link[rel~='icon']"); if (!l) { l = document.createElement('link'); l.rel = 'icon'; document.head.appendChild(l); } l.href = res.data['site.favicon']; }
      if (res.data['site.name']) document.title = res.data['site.name'] + ' - 회원가입';
    }).catch(() => {});
  }, []);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) { setError('이름을 입력해주세요.'); return; }
    if (!form.email.trim()) { setError('아이디를 입력해주세요.'); return; }
    if (form.password.length < 4) { setError('비밀번호는 4자 이상이어야 합니다.'); return; }
    if (form.password !== form.passwordConfirm) { setError('비밀번호가 일치하지 않습니다.'); return; }

    setLoading(true);
    try {
      await api.post('/auth/signup', { email: form.email.trim(), password: form.password, name: form.name.trim(), nickname: form.nickname.trim() || undefined });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.message || '회원가입에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', background: 'var(--tblr-bg-surface, #f4f6fa)', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: 'var(--tblr-bg-surface, #fff)', borderRadius: 16, padding: '48px 40px', maxWidth: 420, width: '100%', textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#d3f9d8', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <IconCheck size={32} style={{ color: '#2f9e44' }} />
          </div>
          <h2 style={{ fontWeight: 700, fontSize: 22, marginBottom: 8 }}>가입 신청 완료</h2>
          <p className="text-muted" style={{ fontSize: 14, lineHeight: 1.7, marginBottom: 24 }}>
            회원가입 신청이 완료되었습니다.<br />관리자 승인 후 로그인이 가능합니다.
          </p>
          <button className="btn btn-primary" style={{ borderRadius: 10, height: 44, width: '100%', fontWeight: 600 }}
            onClick={() => navigate('/login')}>
            로그인 페이지로 이동
          </button>
        </div>
      </div>
    );
  }

  // 회원가입 비활성화 시 안내
  if (siteConfig['site.signupEnabled'] === 'false') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', background: 'var(--tblr-bg-surface, #f4f6fa)', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: 'var(--tblr-bg-surface, #fff)', borderRadius: 16, padding: '48px 40px', maxWidth: 420, width: '100%', textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#fff3cd', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <IconAlertCircle size={32} style={{ color: '#f59f00' }} />
          </div>
          <h2 style={{ fontWeight: 700, fontSize: 22, marginBottom: 8 }}>회원가입 비활성화</h2>
          <p className="text-muted" style={{ fontSize: 14, lineHeight: 1.7, marginBottom: 24 }}>
            현재 회원가입이 비활성화되어 있습니다.<br />관리자에게 문의하세요.
          </p>
          <button className="btn btn-primary" style={{ borderRadius: 10, height: 44, width: '100%', fontWeight: 600 }}
            onClick={() => navigate('/login')}>
            로그인 페이지로 이동
          </button>
        </div>
      </div>
    );
  }

  const inputStyle = {
    paddingLeft: 38, height: 44, borderRadius: 10, fontSize: 14,
    border: '1.5px solid var(--tblr-border-color, #e6e7e9)', transition: 'border-color 0.15s',
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', background: 'var(--tblr-bg-surface, #f4f6fa)' }}>
      {/* 왼쪽 브랜딩 */}
      <div style={{
        flex: 1, background: loginGradient,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: 48, color: '#fff', minWidth: 0,
      }} className="d-none d-lg-flex">
        <div style={{ textAlign: 'center', maxWidth: 400 }}>
          {siteConfig['site.logo'] ? (
            <img src={siteConfig['site.logo']} alt="로고"
              style={{ width: '100%', maxWidth: 300, maxHeight: 100, borderRadius: 20, objectFit: 'contain', margin: '0 auto 24px', display: 'block' }} />
          ) : (
            <div style={{
              width: 80, height: 80, borderRadius: 20, background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 24px', fontSize: 36, fontWeight: 900, border: '2px solid rgba(255,255,255,0.3)',
            }}>{siteConfig['site.name']?.[0] || 'A'}</div>
          )}
          <h1 style={{ fontSize: 36, fontWeight: 800, marginBottom: 12 }}>{siteConfig['site.name']}</h1>
          <p style={{ fontSize: 16, opacity: 0.85, lineHeight: 1.7, marginBottom: 40 }}>
            새로운 계정을 만들어<br />{siteConfig['site.name']} 서비스를 이용하세요.
          </p>
          {['간편한 회원가입 절차', '관리자 승인 후 즉시 이용 가능', '안전한 개인정보 관리'].map((text, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, textAlign: 'left' }}>
              <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <IconShieldCheck size={14} />
              </div>
              <span style={{ fontSize: 14, opacity: 0.9 }}>{text}</span>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 'auto', opacity: 0.6, fontSize: 13 }}>&copy; {new Date().getFullYear()} {siteConfig['site.name']}. All rights reserved.</div>
      </div>

      {/* 오른쪽 가입 폼 */}
      <div style={{ width: '100%', maxWidth: 480, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 40px', background: 'var(--tblr-bg-surface, #fff)' }}>
        <div className="d-lg-none text-center mb-4">
          <span style={{ fontSize: 28, fontWeight: 800, color: 'var(--tblr-primary, #206bc4)' }}>{siteConfig['site.name']}</span>
          <div className="text-muted small mt-1">회원가입</div>
        </div>

        <div style={{ width: '100%', maxWidth: 360 }}>
          <div className="mb-4">
            <h2 style={{ fontWeight: 700, fontSize: 24, marginBottom: 4 }}>회원가입</h2>
            <p className="text-muted" style={{ fontSize: 14 }}>아래 정보를 입력하여 계정을 만드세요.</p>
          </div>

          {error && (
            <div className="alert alert-danger mb-4" style={{ borderRadius: 10, border: 'none', background: '#fff0f0' }}>
              <div className="d-flex align-items-center gap-2">
                <IconAlertCircle size={18} style={{ color: '#d63939', flexShrink: 0 }} />
                <span style={{ fontSize: 14 }}>{error}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="form-label fw-medium" style={{ fontSize: 14 }}>이름</label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--tblr-muted)', pointerEvents: 'none' }}><IconUser size={17} /></span>
                <input type="text" className="form-control" placeholder="이름을 입력하세요" value={form.name} onChange={e => handleChange('name', e.target.value)} required autoFocus style={inputStyle}
                  onFocus={e => e.target.style.borderColor = 'var(--tblr-primary)'} onBlur={e => e.target.style.borderColor = 'var(--tblr-border-color, #e6e7e9)'} />
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label fw-medium" style={{ fontSize: 14 }}>
                닉네임
                <span className="text-muted fw-normal ms-1" style={{ fontSize: 12 }}>(선택 · 없으면 이름으로 표시)</span>
              </label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--tblr-muted)', pointerEvents: 'none' }}><IconUser size={17} /></span>
                <input type="text" className="form-control" placeholder="닉네임을 입력하세요" value={form.nickname} onChange={e => handleChange('nickname', e.target.value)} style={inputStyle}
                  onFocus={e => e.target.style.borderColor = 'var(--tblr-primary)'} onBlur={e => e.target.style.borderColor = 'var(--tblr-border-color, #e6e7e9)'} />
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label fw-medium" style={{ fontSize: 14 }}>아이디</label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--tblr-muted)', pointerEvents: 'none' }}><IconMail size={17} /></span>
                <input type="text" className="form-control" placeholder="아이디를 입력하세요" value={form.email} onChange={e => handleChange('email', e.target.value)} required style={inputStyle}
                  onFocus={e => e.target.style.borderColor = 'var(--tblr-primary)'} onBlur={e => e.target.style.borderColor = 'var(--tblr-border-color, #e6e7e9)'} />
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label fw-medium" style={{ fontSize: 14 }}>비밀번호</label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--tblr-muted)', pointerEvents: 'none' }}><IconLock size={17} /></span>
                <input type={showPw ? 'text' : 'password'} className="form-control" placeholder="비밀번호를 입력하세요" value={form.password} onChange={e => handleChange('password', e.target.value)} required
                  style={{ ...inputStyle, paddingRight: 40 }}
                  onFocus={e => e.target.style.borderColor = 'var(--tblr-primary)'} onBlur={e => e.target.style.borderColor = 'var(--tblr-border-color, #e6e7e9)'} />
                <button type="button" onClick={() => setShowPw(v => !v)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--tblr-muted)' }} tabIndex={-1}>
                  {showPw ? <IconEyeOff size={17} /> : <IconEye size={17} />}
                </button>
              </div>
            </div>
            <div className="mb-4">
              <label className="form-label fw-medium" style={{ fontSize: 14 }}>비밀번호 확인</label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--tblr-muted)', pointerEvents: 'none' }}><IconLock size={17} /></span>
                <input type={showPw ? 'text' : 'password'} className="form-control" placeholder="비밀번호를 다시 입력하세요" value={form.passwordConfirm} onChange={e => handleChange('passwordConfirm', e.target.value)} required style={inputStyle}
                  onFocus={e => e.target.style.borderColor = 'var(--tblr-primary)'} onBlur={e => e.target.style.borderColor = 'var(--tblr-border-color, #e6e7e9)'} />
              </div>
            </div>

            <button type="submit" disabled={loading} style={{
              width: '100%', height: 46, borderRadius: 10, fontSize: 15, fontWeight: 600,
              background: loading ? 'var(--tblr-muted)' : loginPrimary,
              border: 'none', color: '#fff', cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: loading ? 'none' : '0 4px 12px rgba(32,107,196,0.35)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}>
              {loading ? (<><span className="spinner-border spinner-border-sm" style={{ width: 18, height: 18 }} /> 가입 중...</>) : '회원가입'}
            </button>
          </form>

          <div className="text-center mt-4" style={{ fontSize: 14 }}>
            <span className="text-muted">이미 계정이 있으신가요? </span>
            <a href="/login" style={{ color: loginPrimary, fontWeight: 600, textDecoration: 'none' }}
              onClick={e => { e.preventDefault(); navigate('/login'); }}>로그인</a>
          </div>
        </div>

        <div className="d-lg-none mt-auto pt-4 text-muted" style={{ fontSize: 12 }}>&copy; {new Date().getFullYear()} {siteConfig['site.name']}</div>
      </div>
    </div>
  );
}
